import React, { useState, useEffect, useMemo } from 'react';
import { TelemetryPanel, UnitSystem, MenuItem } from '../lib/dashboard-types';
import {
  vizLabel as getVizLabel,
  colKey,
  tableColLabel,
  makeColumns,
  CHART_MAX_UNITS,
  getChannelDef,
  getSubsystemLabel,
} from '../lib/telemetry-helpers';
import {
  unitOf,
  mkVizIcon,
  dotIcon,
  getPanelPrimaryParam,
  getTelemetrySeriesColor,
  resolveTableColumns,
  isNoneUnit,
} from '../../../../../CommonComponents/telemetry';
import { buildAddParameterMenu } from '../lib/telemetry-menu-helpers';
import useTelemetryConfirm from '../hooks/useTelemetryConfirm';
import './telemetry-components.css';
import { InfoSvg } from '../../../../../CommonComponents/svgs/Svgs';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import { checkCommonNameValidation } from '../../../../../../utils/commonFunctions';

interface ConfigDrawerProps {
  panel: TelemetryPanel | null;
  onClose: () => void;
  patchWidget: (id: string, patch: Partial<TelemetryPanel>) => void;
  removeWidget?: (id: string) => void;
  openMenu: (e: React.MouseEvent, items: MenuItem[], opts?: any) => void;
  closeMenu: () => void;
  units: UnitSystem;
}

export const ConfigDrawer: React.FC<ConfigDrawerProps> = ({
  panel,
  onClose,
  patchWidget,
  openMenu,
  closeMenu,
  units,
}) => {
  const [draft, setDraft] = useState<TelemetryPanel | null>(panel);
  const { confirmParamDeletion } = useTelemetryConfirm();

  useEffect(() => {
    if (!panel) {
      setDraft(null);
      return;
    }
    try {
      setDraft(structuredClone(panel));
    } catch {
      setDraft(JSON.parse(JSON.stringify(panel)));
    }
  }, [panel]);

  // Derived metadata from draft
  const primaryParam = useMemo(
    () => (draft ? getPanelPrimaryParam(draft) : ({} as any)),
    [draft]
  );
  const firstSub = primaryParam?.subsystem || '';
  const firstParamId = primaryParam?.paramId || primaryParam?.paramName || '';

  const isStreamTable = draft?.graphMode === 'ctable';
  const isSingleTable = draft?.graphMode === 'table';
  const isTableWidget = isStreamTable || isSingleTable;
  const isMultiChart = draft?.graphMode === 'cchart';
  const isMultiParamWidget = isTableWidget || isMultiChart;

  const cols = useMemo(() => {
    if (!draft) return [];
    return draft.parameters || makeColumns(firstSub, firstParamId);
  }, [draft, firstSub, firstParamId]);

  const hidden = useMemo(() => new Set(draft?.hiddenCols || []), [draft?.hiddenCols]);
  const vis = useMemo(() => cols.filter((c) => !hidden.has(colKey(c))), [cols, hidden]);

  // Compute unique units and series labels for Multi-Chart (Hooks must be called unconditionally)
  const chartUnits: string[] = useMemo(() => {
    if (!isMultiChart || !draft) return [];
    const list: string[] = [];
    vis.forEach((c) => {
      const u = unitOf(getChannelDef(c?.subsystem || '', c?.paramId || c?.paramName || ''), units);
      if (u && !isNoneUnit(u) && !list.includes(u) && list.length < CHART_MAX_UNITS) {
        list.push(u);
      }
    });
    return list;
  }, [isMultiChart, draft, vis, units]);

  const seriesLabel = useMemo(() => {
    if (!isMultiParamWidget || !draft) return '';
    const expandedCols = resolveTableColumns({ ...draft, parameters: vis } as any);
    const totalSeries = expandedCols.length;
    const totalParams = vis.length;
    return totalParams > 1 && totalParams !== totalSeries
      ? `${totalParams} params · ${totalSeries} series`
      : `${totalSeries} series`;
  }, [isMultiParamWidget, draft, vis]);

  // Early return if no panel or draft (called strictly after all hooks)
  if (!panel || !draft) return null;

  const hasExceededParamLimit = (draft.parameters?.length || cols.length) > 65;
  const isTitleEmpty = !draft.name || !draft.name.trim();

  // Compute allowed visualization modes
  const rawAllowed: string[] =
    Array.isArray(primaryParam?.allowed) && primaryParam.allowed.length > 0
      ? primaryParam.allowed
      : ['line', 'area', 'kpi', 'gauge', 'table', 'ctable', 'cchart', 'pie', 'bar', 'hbar', 'radar'];

  const allowedList = [...rawAllowed].sort();
  if (draft.graphMode && !allowedList.includes(draft.graphMode)) {
    allowedList.push(draft.graphMode);
  }

  const allowedModes =
    draft.graphMode === 'kpi'
      ? allowedList.filter((v) => v === 'kpi')
      : allowedList.filter((v) => v !== 'kpi');

  // Form actions
  const patchDraft = (patch: Partial<TelemetryPanel>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!checkCommonNameValidation(e.target.value, 36)) return;
    patchDraft({ name: e.target.value.slice(0, 36) });
  };

  const handleDone = () => {
    if (isTitleEmpty) return;
    const orient = isTableWidget ? (draft.orient || 'cols') : draft.orient;
    const order = isTableWidget && orient === 'rows' ? (draft.order || 'desc') : draft.order;
    patchWidget(panel.id, {
      name: draft.name.trim(),
      graphMode: draft.graphMode,
      parameters: draft.parameters,
      hiddenCols: draft.hiddenCols,
      orient,
      order,
      search: draft.search,
    });
    onClose();
  };

  const handleViz = (v: string) => {
    const patch: Partial<TelemetryPanel> = { graphMode: v };
    if (['table', 'cchart', 'ctable'].includes(v) && !(draft.parameters && draft.parameters.length)) {
      patch.parameters = makeColumns(firstSub, firstParamId);
      patch.name = draft.name || getSubsystemLabel(firstSub);
      patch.hiddenCols = [];
      if (v !== 'cchart') patch.search = '';
    }
    patchDraft(patch);
  };

  const handleOrientChange = (orient: 'rows' | 'cols') => {
    patchDraft({ orient });
  };

  const handleOrderChange = (order: 'desc' | 'asc') => {
    patchDraft({ order });
  };

  const handleToggleParamVisibility = (k: string) => {
    const nextHidden = new Set(draft.hiddenCols || []);
    nextHidden.has(k) ? nextHidden.delete(k) : nextHidden.add(k);
    patchDraft({ hiddenCols: Array.from(nextHidden) });
  };

  const handleMoveParamUp = (i: number) => {
    if (i <= 0) return;
    const list = [...cols];
    const temp = list[i];
    list[i] = list[i - 1];
    list[i - 1] = temp;
    patchDraft({ parameters: list });
  };

  const handleMoveParamDown = (i: number) => {
    if (i >= cols.length - 1) return;
    const list = [...cols];
    const temp = list[i];
    list[i] = list[i + 1];
    list[i + 1] = temp;
    patchDraft({ parameters: list });
  };

  const handleRemoveParam = (k: string, paramLabel?: string) => {
    confirmParamDeletion(paramLabel || k, () => {
      patchDraft({ parameters: cols.filter((x) => colKey(x) !== k) });
    });
  };

  // Add parameter menu popups
  const handleOpenAddMenu = (e: React.MouseEvent, isForChart: boolean) => {
    const r = (e.currentTarget as HTMLElement)?.getBoundingClientRect();
    const mw = 220;
    const anchorPos = r ? { x: Math.max(8, r.right - mw), y: r.bottom + 4 } : undefined;
    const activeChartUnits = isForChart ? chartUnits : [];
    const maxUnits = isForChart ? CHART_MAX_UNITS : 0;
    const menuTitle = isForChart ? 'Add Parameter' : 'Add Column';

    const items = buildAddParameterMenu(
      cols,
      draft.id,
      activeChartUnits,
      maxUnits,
      units,
      (_id, patch) => patchDraft(patch),
      openMenu,
      closeMenu,
      anchorPos,
      draft.graphMode
    );

    openMenu(e, items, { title: menuTitle, width: mw, anchorX: anchorPos?.x, anchorY: anchorPos?.y });
  };

  // Future Enhancement: Threshold Alerts (Preserved for future release)
  // const isThresholdActive = primaryParam?.threshold?.on !== false;

  return (
    <>
      <div className="config-backdrop" onClick={onClose} />
      <div data-nodrag className="td-cfg-drawer">
        {/* Header */}
        <div className="td-cfg-header">
          <div className="td-cfg-title-wrap">
            <div className="td-cfg-title">{draft.name || 'Configure Panel'}</div>
          </div>
          <button onClick={onClose} className="icon-btn icon-btn-md icon-btn-ghost" title="Close">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="td-cfg-body">
          {/* Panel Title */}
          <div className="td-cfg-section">
            <div className="td-cfg-label">Panel Title</div>
            <input
              value={draft.name || ''}
              placeholder="Panel title"
              onChange={handleNameChange}
              className="td-cfg-input"
            />
          </div>

          {/* All other sections rendered only if parameters <= 65 */}
          {!hasExceededParamLimit && (
            <>
              {/* Visualization Selector */}
              <div className="td-cfg-section">
                <div className="td-cfg-label">Visualization</div>
                <div className="td-cfg-chip-group">
                  {allowedModes.map((v) => {
                    const isActive = draft.graphMode === v;
                    return (
                      <button
                        key={v}
                        onClick={() => handleViz(v)}
                        className={`td-cfg-chip ${isActive ? 'td-cfg-chip--active' : ''}`}
                      >
                        {mkVizIcon(v)} {getVizLabel(v)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Table Orientation (for Stream Table and Single Table) */}
              {isTableWidget && (
                <div className="td-cfg-section">
                  <div className="td-cfg-label">Orientation</div>
                  <div className="td-cfg-chip-group">
                    {([['cols', 'Time in columns'], ['rows', 'Time in rows']] as const).map(([k, l]) => (
                      <button
                        key={k}
                        onClick={() => handleOrientChange(k)}
                        className={`td-cfg-chip ${(draft.orient || 'cols') === k ? 'td-cfg-chip--active' : ''}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Table Row Order (Only when Time in rows) */}
              {isTableWidget && (draft.orient || 'cols') === 'rows' && (
                <div className="td-cfg-section">
                  <div className="td-cfg-label">Row Order</div>
                  <div className="td-cfg-chip-group">
                    {([['desc', 'Newest first'], ['asc', 'Oldest first']] as const).map(([k, l]) => (
                      <button
                        key={k}
                        onClick={() => handleOrderChange(k)}
                        className={`td-cfg-chip ${(draft.order || 'desc') === k ? 'td-cfg-chip--active' : ''}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Multi-Chart Unit Warning */}
              {isMultiChart && chartUnits.length >= CHART_MAX_UNITS && (
                <div className="td-cfg-warning-notice">
                  <InfoSvg />
                  At the {CHART_MAX_UNITS}-unit maximum. Remove a parameter or add to a new chart.
                </div>
              )}

              {/* Parameters List (for Stream Table and Multi-Chart) */}
              {isMultiParamWidget && (
                <div className="td-cfg-section">
                  <div className="td-cfg-section-header">
                    <div className="td-cfg-label td-cfg-label-no-margin">
                      Parameters{' '}
                      <CustomToolTip
                        title={
                          isMultiChart
                            ? `(${seriesLabel} · ${chartUnits.length}/${CHART_MAX_UNITS} units)`
                            : `(${seriesLabel})`
                        }
                      >
                        <InfoSvg />
                      </CustomToolTip>
                    </div>
                    <button
                      className="td-cfg-add-btn"
                      onClick={(e) => handleOpenAddMenu(e, isMultiChart)}
                    >
                      + Add Parameter
                    </button>
                  </div>

                  {cols.map((c, i) => {
                    const on = !hidden.has(colKey(c));
                    const k = colKey(c);
                    const label = tableColLabel(c);
                    const seriesColor = getTelemetrySeriesColor(i);

                    return (
                      <div key={k} className={`td-cfg-param-row ${on ? 'td-cfg-param-row--active' : ''}`}>
                        {dotIcon(on ? seriesColor : '#4a4a4a')}
                        <button
                          onClick={() => handleToggleParamVisibility(k)}
                          className={`td-cfg-param-title-btn ${on ? 'td-cfg-param-title-btn--on' : ''}`}
                        >
                          {label}
                        </button>
                        <button
                          disabled={i === 0}
                          onClick={() => handleMoveParamUp(i)}
                          className="td-cfg-icon-btn"
                          title="Move Up"
                        >
                          ▲
                        </button>
                        <button
                          disabled={i === cols.length - 1}
                          onClick={() => handleMoveParamDown(i)}
                          className="td-cfg-icon-btn"
                          title="Move Down"
                        >
                          ▼
                        </button>
                        <button
                          onClick={() => handleRemoveParam(k, label)}
                          className="td-cfg-icon-btn td-cfg-icon-btn--danger"
                          title="Remove Parameter"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Threshold alerts For Future Enhancements: Active for supported chart types */}
              {/* {['line', 'area', 'kpi', 'gauge', 'radial'].includes(draft.graphMode) && isThresholdActive} */}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="td-cfg-footer">
          <button className="td-cfg-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            disabled={isTitleEmpty}
            className="td-cfg-save-btn"
            onClick={handleDone}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
};

export default ConfigDrawer;
